/**
 * Account Delete Feature Hook
 *
 * Manages delete confirmation state and execution.
 */

import { useState, useCallback } from 'react';
import { useDeleteAccount, type Account } from '@/entities/account';
import { logger } from '@/shared/lib';
import type { AccountDeleteState } from './types';

export const useAccountDelete = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const deleteMutation = useDeleteAccount();

  const isDeleting = deleteMutation.isPending;

  const openConfirm = useCallback((account: Account) => {
    setSelectedAccount(account);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelectedAccount(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      await deleteMutation.mutateAsync({
        path: { accountId: selectedAccount.id },
      });
      close();
    } catch (error) {
      logger.error('Failed to delete account:', error);
      throw error;
    }
  }, [selectedAccount, deleteMutation, close]);

  const state: AccountDeleteState = {
    isOpen,
    selectedAccount,
    isDeleting,
    openConfirm,
    close,
    confirm,
  };

  return state;
};
